import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APIS } from "../../../../utils/APIS";
import { GET, POST, PUT, DELETE } from "../../../../utils/Methods";
import { IDS } from "../../../../utils/IDS";
import { Select2Data } from "../../../../utils/common";

// upgrade_role - Get All (Read)
export const GetAll = createAsyncThunk(
  "UserManagement/upgrade_role/get-all",
  async (filter, { rejectWithValue }) => {
    try {
      const res = await GET(
        `${APIS.UserManagement.Upgrade_Role}?name=${filter?.name || ""}&page=${
          filter?.page || IDS.PAGE
        }&per_page=${filter?.per_page || IDS.PERPAGE}&email=${
          filter?.email || ""
        }&contact=${filter?.contact || ""}&start_date=${
          filter?.startDate || ""
        }&end_date=${filter?.endDate || ""}`,
      );
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const GetAllRoles = createAsyncThunk(
  "UserManagement/upgrade_role/get-all-roles",
  async (filter, { rejectWithValue }) => {
    try {
      const res = await GET(`${APIS.UserManagement.Upgrade_Role}/roles`);

      return await Select2Data(res?.data, "role_id");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const GetAllUplineRoles = createAsyncThunk(
  "UserManagement/upgrade_role/get-all-upline-roles",
  async (id, { rejectWithValue }) => {
    try {
      const res = await GET(`${APIS.UserManagement.Upgrade_Role}/roles/${id}`);

      return await Select2Data(res?.data, "new_role_id");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const GetAllUsersByRole = createAsyncThunk(
  "UserManagement/upgrade_role/get-all-users-by-role",
  async (id, { rejectWithValue }) => {
    try {
      const res = await GET(
        `${APIS.UserManagement.Upgrade_Role}/roles/${id}/users`,
      );

      return res?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const UpgradeUserRole = createAsyncThunk(
  "UserManagement/upgrade_role/upgrade-role",
  async (data, { rejectWithValue }) => {
    try {
      const res = await POST(APIS.UserManagement.Upgrade_Role, data);

      if (res?.success) {
        return res;
      }
      return rejectWithValue(res);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// upgrade_role - Get (Read)
export const GetSingle = createAsyncThunk(
  "UserManagement/upgrade_role/get",
  async (id, { rejectWithValue }) => {
    try {
      const res = await GET(`${APIS.UserManagement.Upgrade_Role}/${id}`);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// upgrade_role - Create (Add)
export const Create = createAsyncThunk(
  "UserManagement/upgrade_role/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await POST(APIS.UserManagement.Upgrade_Role, data);

      if (res?.success) {
        return res;
      }
      return rejectWithValue(res);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// upgrade_role - Update
export const Update = createAsyncThunk(
  "UserManagement/upgrade_role/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await PUT(`${APIS.UserManagement.Upgrade_Role}/${id}`, data);
      if (res?.success) {
        return res;
      }
      return rejectWithValue(res);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// upgrade_role - Delete
export const Delete = createAsyncThunk(
  "UserManagement/upgrade_role/delete",
  async (id, { rejectWithValue }) => {
    try {
      const data = await DELETE(`${APIS.UserManagement.Upgrade_Role}/${id}`);
      return id; // Return the id of the deleted upgrade_role
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// upgrade_role - Status
export const StatusUpdate = createAsyncThunk(
  "UserManagement/upgrade_role/status",
  async (id, { rejectWithValue }) => {
    try {
      const res = await POST(`${APIS.UserManagement.Upgrade_Role}/${id}`);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const handleMoney = createAsyncThunk(
  "UserManagement/upgrade_role/status-wildcard",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await POST(
        `${APIS.Wallet.WithdrawReq}/add-remove-points/${id}`,
        data,
      );
      if (res?.success) return res;
      return rejectWithValue(res);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const Slice = createSlice({
  name: "upgrade_role",
  initialState: {
    data: [],
    Roles: [],
    Upline_Roles: [],
    Users_By_Roles: [],
    current_page: IDS.PAGE,
    total_pages: IDS.TOTALPAGES,
    per_page: IDS.PERPAGE,
    total: IDS.TOTAL,
    status: IDS.SLICESTATUS.Idle,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handling all actions for GetSingle, Create, Update, Delete
      .addCase(GetAllUsersByRole.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetAllUsersByRole.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.Users_By_Roles = action.payload || [];
      })
      .addCase(GetAllUsersByRole.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(GetAllUplineRoles.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetAllUplineRoles.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.Upline_Roles = action.payload || [];
      })
      .addCase(GetAllUplineRoles.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(GetAllRoles.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetAllRoles.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.Roles = action.payload || [];
      })
      .addCase(GetAllRoles.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(GetAll.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetAll.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.data = action.payload?.data?.data || [];
        state.current_page = action.payload?.data?.current_page || IDS.PAGE;
        state.total_pages = action.payload?.data?.total_pages || IDS.TOTALPAGES;
        state.per_page = action.payload?.data?.per_page || IDS.PERPAGE;
        state.total = action.payload?.data?.total || IDS.TOTAL;
      })
      .addCase(GetAll.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(GetSingle.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetSingle.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
      })
      .addCase(GetSingle.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(Create.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(Create.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        state.data.unshift(action.payload.data);
      })
      .addCase(Create.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(Update.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(Update.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        const index = state.data.findIndex(
          (upgrade_role) => upgrade_role.id === action.payload?.data?.id,
        );
        if (index !== -1) {
          state.data[index] = action.payload.data;
        }
      })
      .addCase(Update.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(Delete.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(Delete.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        state.data = state.data.filter(
          (upgrade_role) => upgrade_role.id !== action.payload.id,
        ); // Remove the upgrade_role with the deleted id
      })
      .addCase(Delete.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })
      //
      .addCase(StatusUpdate.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(StatusUpdate.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        state.data = state.data.filter(
          (upgrade_role) => upgrade_role.id !== action.payload.id,
        ); // Remove the upgrade_role with the deleted id
      })
      .addCase(StatusUpdate.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      });
  },
});

// Export actions (if needed in components)
export const {} = Slice.actions;

export default Slice.reducer;
